import { SimpleTable } from '@pandiafi/components'
import classNames from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { TRemittance } from '../../../../common/validations/remittance'
import { PaymentsByMonth } from '../../../../common/generated/residentApiSchemas'
import { toShortDate, localizeCurrency, sumTotal } from '../utils'
import styles from './PaymentTable.module.css'

interface RemittanceTableProps {
  allRemittances: TRemittance[]
  paymentOrders: PaymentsByMonth['paymentOrders']
  withholdings: PaymentsByMonth['withholdingsUsed']
  year: string
  monthNumber: string
  paymentsSums: string[]
}

export const RemittanceTable: React.FC<RemittanceTableProps> = ({
  allRemittances,
  paymentOrders,
  withholdings,
  year,
  monthNumber,
  paymentsSums,
}) => {
  const { t } = useTranslation(['payments'])

  const filterISODateStringByMonth = (
    dateString: string,
    selectedYear: string,
    selectedMonth: string // can be single char
  ): boolean => {
    const [yyyy, mm] = dateString.split('-')
    return yyyy === selectedYear && mm === selectedMonth.padStart(2, '0')
  }

  const remittances = allRemittances.filter((r) =>
    filterISODateStringByMonth(r.paidDate, year, monthNumber)
  )

  const monthWithholdings = withholdings.filter((withholding) =>
    filterISODateStringByMonth(withholding.date, year, monthNumber)
  )

  const refundLabel = t('payments:refund')
  const transferLabel = t('payments:transfer')

  const withheldFromLabel = (
    withheldFrom: PaymentsByMonth['withholdingsUsed'][number]['withheldFrom']
  ) => {
    switch (withheldFrom) {
      case 'securityDeposit': {
        return t('payments:withheldFrom.securityDeposit')
      }
      case 'depositVoucher': {
        return t('payments:withheldFrom.depositVoucher')
      }
      case 'rooFee': {
        return t('payments:withheldFrom.rooFee')
      }
    }
  }

  const rows = [
    ...remittances.map((remittance, index) => ({
      key: `remittance-${index}-${remittance.paidDate}-${remittance.nameOfPayer}`,
      title:
        remittance.entryType === 'transfer'
          ? transferLabel
          : remittance.nameOfPayer,
      date: remittance.paidDate,
      amount: Number(remittance.amount),
    })),
    ...paymentOrders.map((paymentOrder, index) => ({
      key: `refund-${paymentOrder.executionDate}-${paymentOrder.amount}-${index}`,
      title: refundLabel,
      date: paymentOrder.executionDate,
      amount: -Number(paymentOrder.amount),
    })),
    ...monthWithholdings.map((withholding, index) => ({
      key: `withholding-${withholding.date}-${withholding.amount}-${index}`,
      title: withheldFromLabel(withholding.withheldFrom),
      date: withholding.date,
      amount: Number(withholding.amount),
    })),
  ].sort((a, b) => b.date.localeCompare(a.date))

  const amounts = rows.map((row) => Number(row.amount.toFixed(2)))
  const remittancesTotal = sumTotal(amounts)
  const saldo = remittancesTotal - sumTotal(paymentsSums.map((s) => Number(s)))

  const hasRows = rows.length > 0

  return (
    <SimpleTable
      className={styles.table}
      header={
        hasRows ? (
          <tr>
            <th>{t('payments:payer')}</th>
            <th className={styles.number}>{t('payments:paidDate')}</th>
            <th className={styles.number}>{t('payments:sum')}</th>
          </tr>
        ) : undefined
      }
      footer={
        <>
          {hasRows ? (
            <tr>
              <td colSpan={2} className={styles.total}>
                {t('payments:sumTotal')}
              </td>
              <td className={classNames(styles.number, styles.total)}>
                {localizeCurrency(remittancesTotal)}
              </td>
            </tr>
          ) : null}
          <tr>
            <td colSpan={2} className={styles.total}>
              {t('payments:saldo')}
            </td>
            <td className={classNames(styles.number, styles.total)}>
              {localizeCurrency(saldo)}
            </td>
          </tr>
        </>
      }
    >
      {hasRows ? (
        rows.map((row) => (
          <tr key={row.key}>
            <td>{row.title}</td>
            <td className={styles.number}>{toShortDate(row.date)}</td>
            <td className={styles.number}>{localizeCurrency(row.amount)}</td>
          </tr>
        ))
      ) : (
        <tr>
          <td colSpan={3}>{t('payments:noRemittances')}</td>
        </tr>
      )}
    </SimpleTable>
  )
}
